import { useState } from 'react';

/**
 * Custom hook for managing uniform add/submit button states across the application
 * Provides consistent UX for "Add to [Category]" -> "Adding..." -> "Added" pattern
 */
export const useAddButtonState = (initialState = 'idle') => {
  const [buttonStates, setButtonStates] = useState(new Map());

  // Button state types
  const BUTTON_STATES = {
    IDLE: 'idle',       // Initial state: "Add to Job"
    LOADING: 'loading', // Processing state: "Adding..." with spinner
    SUCCESS: 'success', // Success state: "Added" with checkmark
    ERROR: 'error'      // Error state: back to idle with error indication
  };

  /**
   * Get the current state of a button by ID
   */
  const getButtonState = (buttonId) => {
    return buttonStates.get(buttonId) || BUTTON_STATES.IDLE;
  };

  /**
   * Set the state of a specific button
   */
  const setButtonState = (buttonId, state) => {
    setButtonStates(prev => new Map(prev.set(buttonId, state)));
  };

  /**
   * Reset a button to idle state
   */
  const resetButtonState = (buttonId) => {
    setButtonState(buttonId, BUTTON_STATES.IDLE);
  };

  /**
   * Reset all buttons to idle state
   */
  const resetAllButtonStates = () => {
    setButtonStates(new Map());
  };

  /**
   * Check if a button is in a specific state
   */
  const isButtonState = (buttonId, state) => {
    return getButtonState(buttonId) === state;
  };

  /**
   * Handle the complete add operation with automatic state management
   * @param {string} buttonId - Unique identifier for the button
   * @param {function} operation - Async function to perform the add operation
   * @param {object} options - Configuration options
   */
  const handleAddOperation = async (buttonId, operation, options = {}) => {
    const {
      successTimeout = 2000, // How long to show "Added" state
      onSuccess,
      onError,
      resetOnError = true
    } = options;

    try {
      // Set loading state
      setButtonState(buttonId, BUTTON_STATES.LOADING);

      // Perform the operation
      const result = await operation();

      // Set success state
      setButtonState(buttonId, BUTTON_STATES.SUCCESS);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result);
      }

      // Auto-reset to idle after timeout (optional)
      if (successTimeout > 0) {
        setTimeout(() => {
          resetButtonState(buttonId);
        }, successTimeout);
      }

      return result;
    } catch (error) {
      // Set error state
      setButtonState(buttonId, BUTTON_STATES.ERROR);

      // Call error callback if provided
      if (onError) {
        onError(error);
      }

      // Reset to idle after showing error (optional)
      if (resetOnError) {
        setTimeout(() => {
          resetButtonState(buttonId);
        }, 3000);
      }

      throw error;
    }
  };

  return {
    BUTTON_STATES,
    getButtonState,
    setButtonState,
    resetButtonState,
    resetAllButtonStates,
    isButtonState,
    handleAddOperation
  };
};

export default useAddButtonState;